import {
  AuthenticationDetails,
  CognitoUser,
  CognitoUserPool,
  type CognitoUserSession,
} from 'amazon-cognito-identity-js';
import type { AuthenticatedSession } from '@cf/shared';
import { authSessionResponseSchema } from '@cf/shared';
import { API_BASE_URL } from '../data/apiBase';
import type { CognitoConfig } from './cognitoConfig';
import type { IAuthClient, SignInResult } from './authClient';

/** Label shown in the user's authenticator app for the enrolled TOTP secret. */
const TOTP_ISSUER = 'Enzo';

/**
 * Real Cognito auth, browser-direct. Runs the USER_SRP_AUTH + software-token
 * TOTP flow against the user pool in `eu-west-2`, then presents the resulting
 * **ID token** as the bearer — the API's CognitoAuthPort verifies that token and
 * resolves tenancy from its `sub`. This is the Stage-G replacement the seam was
 * built for; it lives entirely behind `IAuthClient`, so no screen imports the
 * Cognito SDK.
 *
 * `amazon-cognito-identity-js` is callback-based and STATEFUL: the CognitoUser
 * created in `signIn` carries the challenge into `completeMfa` / `setupMfa`, so
 * the instance holds it between steps. Every callback is bridged to a Promise so
 * the two-step seam contract is preserved.
 */
export class CognitoAuthClient implements IAuthClient {
  private readonly pool: CognitoUserPool;
  private user: CognitoUser | null = null;
  private idToken: string | null = null;

  constructor(config: CognitoConfig) {
    this.pool = new CognitoUserPool({
      UserPoolId: config.userPoolId,
      ClientId: config.clientId,
    });
  }

  signIn(email: string, password: string): Promise<SignInResult> {
    const user = new CognitoUser({ Username: email, Pool: this.pool });
    this.user = user;
    const authDetails = new AuthenticationDetails({ Username: email, Password: password });

    return new Promise<SignInResult>((resolve, reject) => {
      user.authenticateUser(authDetails, {
        // MFA is enforced at the pool, so a password alone must NOT yield a
        // session. If Cognito ever returns one, refuse it — we never skip TOTP.
        onSuccess: () => reject(new Error('Multi-factor authentication is required.')),
        onFailure: (err) => reject(toAuthError(err)),
        // Returning user: an authenticator is already enrolled.
        totpRequired: () => resolve({ kind: 'mfa-required' }),
        // First login: enrol an authenticator, surface the secret to render a QR.
        mfaSetup: () => {
          user.associateSoftwareToken({
            associateSecretCode: (secret: string) => {
              resolve({ kind: 'mfa-setup', secret, otpauthUri: buildOtpauthUri(email, secret) });
            },
            onFailure: (err) => reject(toAuthError(err)),
          });
        },
      });
    });
  }

  completeMfa(code: string): Promise<AuthenticatedSession> {
    const user = this.requireUser();
    return new Promise<AuthenticatedSession>((resolve, reject) => {
      user.sendMFACode(
        code,
        {
          onSuccess: (session) => this.finish(session).then(resolve, reject),
          onFailure: (err) => reject(toAuthError(err)),
        },
        'SOFTWARE_TOKEN_MFA',
      );
    });
  }

  setupMfa(code: string): Promise<AuthenticatedSession> {
    const user = this.requireUser();
    return new Promise<AuthenticatedSession>((resolve, reject) => {
      user.verifySoftwareToken(code, 'authenticator', {
        onSuccess: (session) => this.finish(session).then(resolve, reject),
        onFailure: (err) => reject(toAuthError(err)),
      });
    });
  }

  requestPasswordReset(email: string): Promise<void> {
    const user = new CognitoUser({ Username: email, Pool: this.pool });
    return new Promise<void>((resolve) => {
      // Resolve on BOTH outcomes: never reveal whether an account exists.
      user.forgotPassword({
        onSuccess: () => resolve(),
        onFailure: () => resolve(),
      });
    });
  }

  confirmPasswordReset(email: string, code: string, newPassword: string): Promise<void> {
    const user = new CognitoUser({ Username: email, Pool: this.pool });
    return new Promise<void>((resolve, reject) => {
      user.confirmPassword(code, newPassword, {
        onSuccess: () => resolve(),
        onFailure: () =>
          reject(
            new Error(
              'That code was not recognised or has expired. Request a new code and try again.',
            ),
          ),
      });
    });
  }

  signOut(): Promise<void> {
    this.user?.signOut();
    this.user = null;
    this.idToken = null;
    return Promise.resolve();
  }

  getToken(): string | null {
    return this.idToken;
  }

  /**
   * Store the ID token and resolve the app session from the API. Under the
   * sub->DB model the browser cannot derive firm/adviser/role from the token,
   * so it asks the API (which does the sub lookup) via `/auth/me`.
   */
  private async finish(session: CognitoUserSession): Promise<AuthenticatedSession> {
    this.idToken = session.getIdToken().getJwtToken();
    const res = await fetch(`${API_BASE_URL}/api/v1/auth/me`, {
      headers: { authorization: `Bearer ${this.idToken}` },
    });
    if (!res.ok) {
      throw new Error('Could not establish the session — please sign in again.');
    }
    const body = (await res.json()) as { data: unknown };
    return authSessionResponseSchema.parse(body.data) as AuthenticatedSession;
  }

  private requireUser(): CognitoUser {
    if (this.user === null) {
      throw new Error('Sign in first.');
    }
    return this.user;
  }
}

/** Build the `otpauth://` URI an authenticator app scans to enrol the secret. */
function buildOtpauthUri(email: string, secret: string): string {
  const label = `${encodeURIComponent(TOTP_ISSUER)}:${encodeURIComponent(email)}`;
  const params = new URLSearchParams({
    secret,
    issuer: TOTP_ISSUER,
    algorithm: 'SHA1',
    digits: '6',
    period: '30',
  });
  return `otpauth://totp/${label}?${params.toString()}`;
}

/**
 * Map Cognito errors to user-safe messages. Wrong password and unknown user
 * collapse to one message so the form is not an account-existence oracle.
 */
function toAuthError(err: unknown): Error {
  const name = err instanceof Error ? err.name : '';
  switch (name) {
    case 'NotAuthorizedException':
    case 'UserNotFoundException':
      return new Error('Invalid credentials.');
    case 'CodeMismatchException':
    case 'EnableSoftwareTokenMFAException':
      return new Error('That code was incorrect. Try the current code from your app.');
    case 'ExpiredCodeException':
      return new Error('That code has expired. Enter the current code from your app.');
    case 'LimitExceededException':
    case 'TooManyRequestsException':
      return new Error('Too many attempts. Please wait a moment and try again.');
    default:
      return new Error('Sign-in failed. Please try again.');
  }
}
